import { apiGateway } from '@va/api-client';
import { getInstanceId } from '@va/dashboard/selectors/app';
import { useCurrentPeriodFilter, usePreviousPeriodFilter } from '@va/dashboard/util-hooks';
import { post } from '@va/http-client';
import { useFiltersContext } from '@va/shared/feature-filters';
import { getConfig } from '@va/standalone/shared/api-client';
import { getPercentageChange } from '@va/util/helpers';
import { useAsyncFunction, useFetchData } from '@va/util/hooks';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { SWRConfiguration } from 'swr';
import { FunnelDetails, FunnelType } from './funnels.types';

export const useGetFunnelDetails = (
  funnelId?: string | null,
  config?: SWRConfiguration,
  filters?: Record<string, unknown>,
) => {
  const { websiteId, from, until, unit } = useCurrentPeriodFilter();
  const previousFilters = usePreviousPeriodFilter();
  const queryData = useMemo(
    () => ({
      from,
      until,
      unit,
      previousFrom: previousFilters.from,
      previousUntil: previousFilters.until,
    }),
    [from, previousFilters.from, previousFilters.until, unit, until],
  );

  const fetcher = useCallback(async (url: string, queryData: Record<string, any>, body: Record<string, any>) => {
    return post(url, queryData, body);
  }, []);

  const mapperFn = useCallback((funnel: FunnelDetails) => {
    const {
      previousConversionRate,
      conversionRate,
      previousConversion,
      currentConversion,
      visitorCountInTimeRange,
      visitorCountInPreviousTimeRange,
    } = funnel;

    return {
      ...funnel,
      conversionRatePercentageChange: getPercentageChange(previousConversionRate, conversionRate),
      conversionPercentageChange: getPercentageChange(previousConversion, currentConversion),
      visitorPercentageChange: getPercentageChange(visitorCountInPreviousTimeRange, visitorCountInTimeRange),
      levels: funnel.levels,
    };
  }, []);

  return useFetchData<FunnelDetails>(
    funnelId ? [`/v2/websites/${websiteId}/funnelsv2/${funnelId}`, queryData, filters] : null,
    config,
    (res) => mapperFn(res.payload),
    fetcher,
  );
};

export const useCreateFunnel = () => {
  const websiteId = useSelector(getInstanceId);

  const createFunnel = useCallback(
    (funnelData: FunnelType) => {
      const formattedLevels = funnelData.levels.map((level) => ({
        data: level.data,
        kind: level.kind,
        name: level.name,
      }));

      const requestBody = {
        ...funnelData,
        levels: formattedLevels,
      };

      return apiGateway.post(`v1/websites/${websiteId}/funnels`, requestBody, getConfig());
    },
    [websiteId],
  );

  return useAsyncFunction<typeof createFunnel>(createFunnel);
};

export const useGetFunnels = () => {
  const { websiteId, from, until, unit } = useCurrentPeriodFilter();
  const previousFilters = usePreviousPeriodFilter();
  const { appliedFilterValues } = useFiltersContext();
  const url = useMemo(() => `/v2/websites/${websiteId}/funnelsv2`, [websiteId]);

  const queryData = useMemo(
    () => ({
      from,
      until,
      unit,
      previousFrom: previousFilters.from,
      previousUntil: previousFilters.until,
      ...appliedFilterValues,
    }),
    [appliedFilterValues, from, previousFilters.from, previousFilters.until, unit, until],
  );

  const fetcher = useCallback((url: string, query: Record<string, any>) => {
    return post(url, query, {});
  }, []);

  const mapperFn = useCallback((funnels: FunnelDetails[]) => {
    return funnels?.map((funnel) => {
      const {
        previousConversionRate,
        conversionRate,
        previousConversion,
        currentConversion,
        visitorCountInTimeRange,
        visitorCountInPreviousTimeRange,
      } = funnel;

      return {
        ...funnel,
        conversionRatePercentageChange: getPercentageChange(previousConversionRate, conversionRate),
        conversionPercentageChange: getPercentageChange(previousConversion, currentConversion),
        visitorPercentageChange: getPercentageChange(visitorCountInPreviousTimeRange, visitorCountInTimeRange),
        levelsCount: funnel?.levels?.length ?? 0,
        levels: funnel?.levels ?? [],
      };
    });
  }, []);

  return useFetchData<FunnelDetails[]>(
    [url, queryData],
    { revalidateOnMount: true, revalidateIfStale: true },
    (res) => mapperFn(res.payload ?? []),
    fetcher,
  );
};

export const useDeleteFunnels = () => {
  const { websiteId } = useCurrentPeriodFilter();

  const asyncFunc = useCallback(
    (funnelIds: string[]) => {
      return apiGateway.post(`v1/websites/${websiteId}/funnels/bulk-delete`, { funnelIds }, getConfig());
    },
    [websiteId],
  );

  return useAsyncFunction<typeof asyncFunc>(asyncFunc, { immediate: false });
};

export const useEditFunnel = () => {
  const websiteId = useSelector(getInstanceId);

  const asyncFunc = useCallback(
    (funnelId: string, data: Record<string, unknown>) => {
      return apiGateway.patch(`v1/websites/${websiteId}/funnels/${funnelId}`, data, getConfig());
    },
    [websiteId],
  );

  return useAsyncFunction<typeof asyncFunc>(asyncFunc, { immediate: false });
};
